import * as React from 'react'
import {
  Box, Typography, Button, TextField, InputAdornment, FormControl, Select, MenuItem,
  Table, TableHead, TableBody, TableRow, TableCell, TablePagination, Chip, Alert,
} from '@mui/material'
import {useSnackbar} from 'notistack'
import SearchIcon from '@mui/icons-material/Search'
import SearchOffIcon from '@mui/icons-material/SearchOff'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined'
import AcUnitOutlinedIcon from '@mui/icons-material/AcUnitOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ImageSearchOutlinedIcon from '@mui/icons-material/ImageSearchOutlined'

import {
  sectionCardSx, kpiCardSx, kpiValueSx, dataGridWrapSx, headerCellSx, cellSx,
  filterBarSx, primaryBtnSx, secondaryBtnSx,
} from '../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../styles/foundation/typography'
import {NAVY, SUBTLE, SUCCESS, WARNING, ERROR, INFO, DISABLED} from '../../../styles/foundation/colors'
import {GAP} from '../../../styles/foundation/spacing'

import {int, kg, pct} from './format'
import {RULES} from './data/reference'
import {DWS_TWO_SOURCES} from './data/dwsCopy'
import {EVIDENCE_LEVEL} from '../shared/evidenceScore'
import {
  dwsEvidence, dwsRecordsFor, dwsStats, filterSkus, coverageCounts, buildEvidenceIndex, COVERAGE_FILTERS,
} from './state/dwsEvidence'
import DwsShotList from './DwsShotList'
import DwsUploadDrawer from './DwsUploadDrawer'
import DwsRecordDrawer from './DwsRecordDrawer'
import DwsThumb from './components/DwsThumb'
import CoverageBar from './components/CoverageBar'
import EmptyState from './components/EmptyState'
import {chipSx} from './components/tone'

const PER_PAGE = 8
const SEARCH_DEBOUNCE_MS = 300

const labelSx = {fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}
const subtleTextSx = {fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}
const alertSx = {fontFamily: FONT, fontSize: SIZE['2xs'], py: 0.85, '& .MuiAlert-message': {lineHeight: 1.6}}

// The coverage Select's menu is a body-level portal, outside the page's zoom —
// left alone it paints 16px against a 9.6px closed control (see disputes-v2/FilterBar.js).
const selectMenuProps = {PaperProps: {sx: {'& .MuiMenuItem-root': {fontSize: SIZE['2xs']}}}}

/**
 * DWS data — Weight Freeze (v2) Section 3 of 6, Task 7. Tiles, table, gallery
 * and the two drawers (Task 6's Evidence Studio, and the already-built
 * DwsRecordDrawer) that let a merchant see the courier's own scanner
 * readings alongside their own evidence, SKU by SKU.
 *
 * Rows come straight off the shared store (`skus`, from
 * state/useFreezeStore.js) rather than the frozen `SKUS` import, exactly
 * like every other registered section. This is safe because
 * `useFreezeStore.js`'s `ADD_UPLOAD` reducer only ever appends to
 * `state.uploads` — it no longer touches any row's `merchantCount`. So
 * `state/dwsEvidence.js`'s own `merchant = row.merchantCount + <uploads for
 * this row>` sees `row.merchantCount` as the untouched seed baseline exactly
 * once, and this session's uploads are added on top exactly once, no matter
 * whether the row came from the store or the frozen dataset. A Catalogue-side
 * edit to a SKU (fix-code, merge, cat/sub reassignment) now reaches this page
 * within the same session, the same way it reaches every other section.
 */
export default function DwsPage({skus, uploads, actions, onNavigate, focusFallbackRef}) {
  const {enqueueSnackbar} = useSnackbar()

  const [search, setSearch] = React.useState('')
  const [query, setQuery] = React.useState('') // debounced mirror of `search`
  const [coverage, setCoverage] = React.useState('all')
  const [page, setPage] = React.useState(0)
  const [selectedId, setSelectedId] = React.useState('')
  const [uploadOpen, setUploadOpen] = React.useState(false)
  const [uploadRowId, setUploadRowId] = React.useState(null)
  const [openRecord, setOpenRecord] = React.useState(null)

  // The mockup's Search had no debounce, and every keystroke re-ran a filter
  // that calls dwsEvidence() per row. 300 ms is the house default.
  React.useEffect(() => {
    const t = setTimeout(() => setQuery(search), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [search])

  // Filtering changes which rows exist, so page 1 is the only page that is
  // certainly still valid. Selection deliberately does NOT reset the page.
  React.useEffect(() => { setPage(0) }, [query, coverage])

  const stats = React.useMemo(() => dwsStats(skus, uploads), [skus, uploads])
  const counts = React.useMemo(() => coverageCounts(skus, uploads), [skus, uploads])
  const rows = React.useMemo(() => filterSkus(skus, {q: query, coverage}, uploads), [skus, query, coverage, uploads])

  // dwsEvidence otherwise runs inside the filter predicate above, the sort
  // comparator inside filterSkus, AND once per rendered cell below for the
  // very same rows on the very same keystroke — buildEvidenceIndex exists
  // for exactly this (see its own doc comment in dwsEvidence.js).
  const evidenceIndex = React.useMemo(() => buildEvidenceIndex(rows, uploads), [rows, uploads])

  // Resolved against the WHOLE catalogue, never against `rows`. Narrowing the
  // filter under an open gallery must not close it — the merchant asked to
  // see that SKU, and a filter change is not a request to stop showing it.
  const selected = React.useMemo(() => skus.find((s) => s.sku === selectedId) || null, [skus, selectedId])
  const records = React.useMemo(() => (selected ? dwsRecordsFor(selected, uploads) : []), [selected, uploads])
  const evidence = selected ? dwsEvidence(selected, uploads) : null

  const pageCount = Math.max(1, Math.ceil(rows.length / PER_PAGE))
  const clampedPage = Math.min(page, pageCount - 1)
  const pageRows = rows.slice(clampedPage * PER_PAGE, clampedPage * PER_PAGE + PER_PAGE)

  const jumpTo = (filterId) => {
    setCoverage(filterId)
    const el = document.getElementById('dws-table')
    if (el) el.scrollIntoView({block: 'start', behavior: 'smooth'})
  }

  const openUpload = (rowIdArg) => {
    setUploadRowId(rowIdArg || null)
    setUploadOpen(true)
  }

  // Same focus-recovery fallback the rest of the module's drawers use (see
  // CataloguePage/SubcategoryPanel) — reused rather than a second mechanism.
  // Neither of this page's own drawer triggers ("Upload evidence", "Add
  // images") vanishes when its drawer closes, so MUI's own focus-restore
  // should already cover the normal case; this is the same defensive
  // backstop the rest of the module applies unconditionally.
  const closeUploadDrawer = React.useCallback(() => {
    setUploadOpen(false)
    if (typeof window === 'undefined' || typeof document === 'undefined') return
    window.requestAnimationFrame(() => {
      if (document.activeElement === document.body && focusFallbackRef && focusFallbackRef.current) {
        focusFallbackRef.current.focusActive()
      }
    })
  }, [focusFallbackRef])

  const closeRecordDrawer = React.useCallback(() => {
    setOpenRecord(null)
    if (typeof window === 'undefined' || typeof document === 'undefined') return
    window.requestAnimationFrame(() => {
      if (document.activeElement === document.body && focusFallbackRef && focusFallbackRef.current) {
        focusFallbackRef.current.focusActive()
      }
    })
  }, [focusFallbackRef])

  // One evidence record per save: the video, the images and the readings for
  // a single shipment, which is the unit Prozo reviews.
  const handleSaved = (record, score) => {
    // ONLY the store's `uploads` moves (via addUpload) — this must never
    // touch sorterCount, and addUpload itself never touches merchantCount
    // (see useFreezeStore.js). Crediting a merchant upload to the courier's
    // sorter count would make a SKU look scanned when the courier has never
    // scanned it.
    actions.addUpload(record)
    setUploadOpen(false)
    setSelectedId(record.sku)

    const bits = []
    if (record.video) bits.push('packing video')
    if (record.images.length) bits.push(`${int(record.images.length)} image${record.images.length === 1 ? '' : 's'}`)

    // `uploads` here is still the PRE-dispatch array (the store update from
    // actions.addUpload above lands on a later render), so merchant + 1 is
    // exactly the count this save is about to produce.
    const target = skus.find((s) => s.sku === record.sku)
    const after = target ? dwsEvidence(target, uploads).merchant + 1 : 1

    enqueueSnackbar(
      `Evidence saved for ${record.awb}: ${bits.join(' and ')} on ${record.sku}. `
      + `Evidence strength ${score.score} of 100, about ${score.win}% approval. `
      + `${record.sku} now has ${int(after)} merchant record${after === 1 ? '' : 's'} on file.`,
      {variant: score.level === EVIDENCE_LEVEL.WEAK ? 'warning' : 'success', autoHideDuration: 7000},
    )
  }

  const mine = records.filter((r) => r.source === 'upload').length
  const fromCourier = records.length - mine

  // Retention is already applied in the frozen dataset, so the honest
  // sentence is "here is what is retained, and here is how much sits outside
  // it" — not the mockup's arithmetic, which assumed min(sorterCount, 10)
  // records existed. One shipped SKU has 78 sorter readings and 5 records on
  // file.
  const caption = evidence && evidence.beyondRetention > 0
    ? `${int(fromCourier)} courier sorter record${fromCourier === 1 ? '' : 's'} and ${int(mine)} of your own, out of this SKU's ${int(selected.ships)} shipments. `
      + `${int(evidence.beyondRetention)} older sorter reading${evidence.beyondRetention === 1 ? '' : 's'} `
      + `${evidence.beyondRetention === 1 ? 'falls' : 'fall'} outside the ${int(RULES.DWS_RETENTION_RECORDS)}-record retention window. Open any record for the full readings.`
    : `${int(fromCourier)} courier sorter record${fromCourier === 1 ? '' : 's'} and ${int(mine)} of your own, out of this SKU's ${int(selected ? selected.ships : 0)} shipments. Open any record for the full readings.`

  const tile2Tone = stats.both >= stats.skus / 2 ? SUCCESS : WARNING

  return (
    <Box sx={{display: 'grid', gap: GAP.lg}}>
      {/* Header */}
      <Box sx={{display: 'flex', flexDirection: {xs: 'column', sm: 'row'}, justifyContent: 'space-between', alignItems: {xs: 'flex-start', sm: 'center'}, gap: 1.5}}>
        <Box>
          <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.md, color: NAVY}}>
            DWS data
          </Typography>
          <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 0.25, maxWidth: 640}}>
            What the courier&apos;s scanner recorded per SKU, and the evidence you upload against it.
          </Typography>
        </Box>
        <Button
          sx={primaryBtnSx}
          startIcon={<CloudUploadOutlinedIcon sx={{fontSize: 16}} />}
          onClick={() => openUpload(null)}
        >
          Upload evidence
        </Button>
      </Box>

      {/* Two sources, non-dismissible. */}
      <Alert severity='info' sx={alertSx}>
        <Box component='span' sx={{fontWeight: WEIGHT.bold}}>{DWS_TWO_SOURCES.lead}</Box> {DWS_TWO_SOURCES.body}
      </Alert>

      {/* Four KPI tiles — CSS grid with alignItems: 'start' so a tile sizes
          to its own content instead of stretching to match its tallest
          sibling (kpiCardSx carries height: '100%', which is exactly the
          over-tall cards the merchant flagged twice). Padding overridden
          down for density, matching SopPage's/CataloguePage's own tile
          block. Tiles 1-2 are buttons that jump to the table and set the
          coverage filter; 3-4 are static. */}
      <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)'}, gap: GAP.md, alignItems: 'start'}}>
        <Box
          component='button'
          onClick={() => jumpTo('nomev')}
          aria-label={`${int(stats.noMerchant)} SKUs you cannot freeze until you upload evidence. Jump to the table, filtered to them.`}
          sx={{
            ...kpiCardSx(stats.noMerchant > 0 ? ERROR : SUCCESS), p: 1.25, height: 'auto',
            textAlign: 'left', cursor: 'pointer', font: 'inherit', appearance: 'none',
          }}
        >
          <Typography sx={{...kpiValueSx(stats.noMerchant > 0 ? ERROR : SUCCESS), fontSize: '18px', lineHeight: 1.25}}>
            {int(stats.noMerchant)}
          </Typography>
          <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.medium, color: SUBTLE, lineHeight: 1.4, mt: 0.5}}>
            SKUs you cannot freeze until you upload evidence
          </Typography>
        </Box>

        <Box
          component='button'
          onClick={() => jumpTo('both')}
          aria-label={`${int(stats.both)} SKUs with both a sorter reading and your evidence. Jump to the table, filtered to them.`}
          sx={{
            ...kpiCardSx(tile2Tone), p: 1.25, height: 'auto',
            textAlign: 'left', cursor: 'pointer', font: 'inherit', appearance: 'none',
          }}
        >
          <Typography sx={{...kpiValueSx(tile2Tone), fontSize: '18px', lineHeight: 1.25}}>{int(stats.both)}</Typography>
          <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.medium, color: SUBTLE, lineHeight: 1.4, mt: 0.5}}>
            SKUs with both a sorter reading and your evidence
          </Typography>
        </Box>

        <Box sx={{...kpiCardSx(INFO), p: 1.25, height: 'auto'}}>
          <Typography sx={{...kpiValueSx(INFO), fontSize: '18px', lineHeight: 1.25}}>{int(stats.merchantRecords)}</Typography>
          <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.medium, color: SUBTLE, lineHeight: 1.4, mt: 0.5}}>
            Merchant evidence records on file, across {int(stats.skus)} SKUs
          </Typography>
        </Box>

        <Box sx={{...kpiCardSx(INFO), p: 1.25, height: 'auto'}}>
          <Typography sx={{...kpiValueSx(INFO), fontSize: '18px', lineHeight: 1.25}}>{pct(stats.coveragePct)}</Typography>
          <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.medium, color: SUBTLE, lineHeight: 1.4, mt: 0.5}}>
            Of your shipments carry a courier sorter reading
          </Typography>
        </Box>
      </Box>

      <DwsShotList
        coveragePct={stats.coveragePct}
        noMerchantCount={stats.noMerchant}
        skuCount={stats.skus}
        onOpenUpload={() => openUpload(null)}
      />

      {/* Table card */}
      <Box id='dws-table' sx={sectionCardSx}>
        <Box sx={{...filterBarSx, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 1.5, mt: 0}}>
          <TextField
            size='small'
            placeholder='Search SKU or sub-category'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon sx={{fontSize: 16, color: SUBTLE}} />
                </InputAdornment>
              ),
            }}
            inputProps={{'aria-label': 'Search DWS SKUs'}}
            sx={{fontFamily: FONT, fontSize: SIZE['2xs'], minWidth: 260}}
          />

          <FormControl size='small' sx={{fontFamily: FONT, fontSize: SIZE['2xs'], minWidth: 220}}>
            <Select
              value={coverage}
              onChange={(e) => setCoverage(e.target.value)}
              inputProps={{'aria-label': 'Filter by coverage'}}
              MenuProps={selectMenuProps}
            >
              {COVERAGE_FILTERS.map((f) => (
                <MenuItem key={f.id} value={f.id}>{f.label} ({int(counts[f.id])})</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, ml: 'auto', whiteSpace: 'nowrap'}}>
            {int(rows.length)} of {int(skus.length)} SKUs, lowest coverage first
          </Typography>
        </Box>

        {rows.length === 0 ? (
          <EmptyState
            icon={SearchOffIcon}
            title='No SKUs match'
            body='Try a different search, or clear the coverage filter.'
            action={<Button sx={secondaryBtnSx} onClick={() => { setSearch(''); setCoverage('all') }}>Clear filters</Button>}
          />
        ) : (
          <Box sx={dataGridWrapSx}>
            <Table sx={{minWidth: 960}}>
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>SKU</TableCell>
                  <TableCell sx={headerCellSx}>Sub-category</TableCell>
                  <TableCell sx={{...headerCellSx, textAlign: 'right'}}>Shipments</TableCell>
                  <TableCell sx={{...headerCellSx, textAlign: 'right'}}>Sorter images</TableCell>
                  <TableCell sx={{...headerCellSx, textAlign: 'right'}}>Merchant evidence</TableCell>
                  <TableCell sx={headerCellSx}>Sorter coverage</TableCell>
                  <TableCell sx={{...headerCellSx, textAlign: 'right'}}>Gallery</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pageRows.map((row) => {
                  const e = evidenceIndex.get(row.sku)
                  const isSelected = row.sku === selectedId
                  return (
                    <TableRow key={row.sku} hover selected={isSelected}>
                      <TableCell sx={cellSx}>
                        <Typography
                          title={row.sku}
                          sx={{
                            fontFamily: 'monospace', fontWeight: WEIGHT.bold, fontSize: SIZE.xs, color: NAVY,
                            maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}
                        >
                          {row.sku}
                        </Typography>
                        <Typography sx={{...subtleTextSx, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                          {row.product}
                        </Typography>
                      </TableCell>

                      <TableCell sx={cellSx}>
                        <Chip size='small' label={row.sub} sx={chipSx('neutral')} />
                      </TableCell>

                      <TableCell sx={{...cellSx, textAlign: 'right'}}>
                        <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: NAVY}}>{int(row.ships)}</Typography>
                      </TableCell>

                      {/* Two sources, TWO columns — see this page's own
                          header comment on why: a single combined "with
                          evidence" count would hide whether the missing half
                          is the courier's or the merchant's, and those two
                          gaps are closed by two entirely different people. */}
                      <TableCell sx={{...cellSx, textAlign: 'right'}}>
                        {e.sorter === 0 ? (
                          <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: DISABLED}}>None</Typography>
                        ) : (
                          <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: NAVY}}>{int(e.sorter)}</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{...cellSx, textAlign: 'right'}}>
                        {e.merchant === 0 ? (
                          <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.semibold, fontSize: SIZE.xs, color: ERROR}}>None</Typography>
                        ) : (
                          <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.semibold, fontSize: SIZE.xs, color: SUCCESS}}>{int(e.merchant)}</Typography>
                        )}
                      </TableCell>

                      <TableCell sx={cellSx}>
                        <CoverageBar value={e.sorterPct} />
                      </TableCell>

                      <TableCell sx={{...cellSx, textAlign: 'right'}}>
                        {isSelected ? (
                          <Chip size='small' label='Showing' sx={chipSx('info')} />
                        ) : (
                          <Button size='small' sx={secondaryBtnSx} onClick={() => setSelectedId(row.sku)}>
                            View evidence
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>

            <TablePagination
              component='div'
              rowsPerPageOptions={[PER_PAGE]}
              count={rows.length}
              rowsPerPage={PER_PAGE}
              page={clampedPage}
              onPageChange={(e, next) => setPage(next)}
              sx={{
                '& .MuiTablePagination-toolbar': {fontFamily: FONT},
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {fontFamily: FONT, fontSize: SIZE['2xs']},
              }}
            />
          </Box>
        )}
      </Box>

      {/* Gallery card — three states. */}
      <Box id='dws-gallery' sx={sectionCardSx}>
        {!selected ? (
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1, color: SUBTLE}}>
            <InfoOutlinedIcon sx={{fontSize: 16}} />
            <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}}>
              Choose a SKU above to see the courier&apos;s sorter images and your uploads side by side.
            </Typography>
          </Box>
        ) : records.length === 0 ? (
          <EmptyState
            icon={ImageSearchOutlinedIcon}
            title={`No DWS records on file for ${selected.sku}`}
            body='Neither the courier nor you have any evidence on file for this SKU yet. Upload a packing video or stills to start its evidence locker.'
            action={(
              <Button sx={primaryBtnSx} startIcon={<AddPhotoAlternateOutlinedIcon sx={{fontSize: 16}} />} onClick={() => openUpload(selected.sku)}>
                Upload DWS images
              </Button>
            )}
          />
        ) : (
          <Box sx={{display: 'grid', gap: 1.5}}>
            <Box sx={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, justifyContent: 'space-between'}}>
              <Box sx={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75}}>
                <Typography sx={{fontFamily: 'monospace', fontWeight: WEIGHT.bold, fontSize: SIZE.sm, color: NAVY}}>
                  {selected.sku}
                </Typography>
                <Chip size='small' label={selected.sub} sx={chipSx('neutral')} />
                <Chip size='small' label={`${int(selected.ships)} shipments`} sx={chipSx('info')} />
                <Chip
                  size='small'
                  label={`${pct(evidence.sorterPct)} sorter coverage`}
                  sx={chipSx(evidence.sorterPct >= 60 ? 'ok' : evidence.sorterPct >= 30 ? 'warn' : 'danger')}
                />
                <Chip size='small' label={`Most probable weight ${kg(selected.mpw)}`} sx={chipSx('neutral')} />
              </Box>
              <Box sx={{display: 'flex', gap: 1}}>
                <Button size='small' sx={secondaryBtnSx} startIcon={<AddPhotoAlternateOutlinedIcon sx={{fontSize: 15}} />} onClick={() => openUpload(selected.sku)}>
                  Add images
                </Button>
                <Button size='small' sx={primaryBtnSx} startIcon={<AcUnitOutlinedIcon sx={{fontSize: 15}} />} onClick={() => onNavigate('initiate')}>
                  Use in a freeze request
                </Button>
              </Box>
            </Box>

            <Typography sx={labelSx}>{caption}</Typography>

            <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 1.5}}>
              {records.map((r) => (
                <DwsThumb key={r.id} record={r} onOpen={setOpenRecord} />
              ))}
            </Box>
          </Box>
        )}
      </Box>

      <DwsUploadDrawer
        open={uploadOpen}
        rowId={uploadRowId}
        rows={skus}
        uploads={uploads}
        onClose={closeUploadDrawer}
        onSaved={handleSaved}
      />

      <DwsRecordDrawer record={openRecord} row={selected} onClose={closeRecordDrawer} />
    </Box>
  )
}
